'use client';
import type { Assign } from '@ark-ui/react';
import { Splitter } from '@ark-ui/react';
import { type SplitterVariantProps, css, cx, splitter } from '@terra/ui-styled-system';
import type { ComponentProps, HTMLStyledProps } from '@terra/ui-styled-system';
import { forwardRef } from 'react';
import { createStyleContext } from './utils/create-style-context';

const { withProvider, withContext } = createStyleContext(splitter);

export type RootProviderProps = ComponentProps<typeof RootProvider>;
export const RootProvider = withProvider<
  HTMLDivElement,
  Assign<
    Assign<HTMLStyledProps<'div'>, Splitter.RootProviderProps>,
    SplitterVariantProps
  >
>(Splitter.RootProvider, 'root');

export type RootProps = ComponentProps<typeof Root>;
export const Root = withProvider<
  HTMLDivElement,
  Assign<
    Assign<HTMLStyledProps<'div'>, Splitter.RootProps>,
    SplitterVariantProps
  >
>(Splitter.Root, 'root');

export const Panel = withContext<
  HTMLDivElement,
  Assign<HTMLStyledProps<'div'>, Splitter.PanelProps>
>(Splitter.Panel, 'panel');

const BaseResizeTrigger = withContext<
  HTMLButtonElement,
  Assign<HTMLStyledProps<'button'>, Splitter.ResizeTriggerProps>
>(Splitter.ResizeTrigger, 'resizeTrigger');

type ResizeTriggerProps = Assign<
  HTMLStyledProps<'button'>,
  Splitter.ResizeTriggerProps
> & {
  variant?: 'default' | 'interactive';
};

const interactiveResizeTriggerClassName = css({
  '&:hover::after': {
    background: 'var(--colors-border-neutral-focus)',
    width: '1px',
  },
  '&[data-resizing]::after, &[data-dragging]::after, &[data-state="dragging"]::after':
    {
      background: 'var(--colors-border-neutral-focus)',
      width: '2px',
    },
});

export const ResizeTrigger = forwardRef<HTMLButtonElement, ResizeTriggerProps>(
  function ResizeTrigger({ variant = 'default', className, ...props }, ref) {
    const isInteractive = variant === 'interactive';
    return (
      <BaseResizeTrigger
        ref={ref}
        width={isInteractive ? '1px' : undefined}
        bg={isInteractive ? 'transparent' : undefined}
        position={isInteractive ? 'relative' : undefined}
        cursor={isInteractive ? 'col-resize' : undefined}
        flexShrink={isInteractive ? 0 : undefined}
        _active={
          isInteractive
            ? {
                _after: {
                  bg: 'border.neutral.focus',
                  width: '2px',
                },
              }
            : undefined
        }
        _after={
          isInteractive
            ? {
                content: '""',
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: '50%',
                transform: 'translateX(-50%)',
                width: '1px',
                bg: 'transparent',
              }
            : undefined
        }
        _before={
          isInteractive
            ? {
                content: '""',
                position: 'absolute',
                inset: '0 -6px 0 -6px',
                cursor: 'col-resize',
              }
            : undefined
        }
        className={cx(
          isInteractive ? interactiveResizeTriggerClassName : undefined,
          className,
        )}
        {...props}
      />
    );
  },
);

export { SplitterContext as Context } from '@ark-ui/react';
